/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { isMobile, isBrowser } from 'react-device-detect';
import BigNumber from 'bignumber.js';
import ModalBodyLayout from '../../shared/ModalBodyLayout';
import { CARD_LINKABLE_WALLETS, CARD_LINKED_WALLETS } from '../queries';
import { CARD_UPDATE_LINKED_WALLETS } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { RATES_QUERY_KEY, USER_PREFERENCES } from '../../../utilities/sharedQueries';
import { WalletPopupItemPlaceholder } from '../../../components/Placeholders';
import { formatNumber } from '../../../utilities/formatHelpers';
import { currencies, currenciesDefault, exchange } from '../../../utilities/constants';
import WalletWithCheckbox from '../../../components/WalletWithCheckbox';
import {
  useLedgerFilteredWallets,
  useLedgerFilteredLinkableWallets,
} from '../../../utilities/sharedHooks/ledgerWallets';
import { WALLETS_ALL } from '../../Accounts/queries';
import { useRate } from '../../../utilities/sharedHooks/rates';
import { useCardStatus, useUserCardProvider } from '../../../utilities/sharedHooks/user';
import { cardProviders, DEFAULT_FIAT_CURRENCY } from '../constants';
import { BANK_REQUESTS_COINS } from '../../Accounts/constants';
import { MXP_CARD_MANAGEMENT_CONNECT_ACCOUNT } from '../mixpanel-events';
import { FLOWS, sendStats } from '../../../services/mixpanel';

const LinkWallets = ({ hidePopup, cryptodraftDetails }) => {
  const { t: translate } = useTranslation();
  const {
    data: linkableWallets,
    refetch: getLinkableWallets,
    isLoading: isLoadingLinkableWallets,
  } = useLedgerFilteredLinkableWallets(CARD_LINKABLE_WALLETS);
  const { refetch: getLinkedWallets } = useQuery(CARD_LINKED_WALLETS);
  const { data: rates, isLoading: isLoadingRates } = useQuery(RATES_QUERY_KEY);
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { data: ledgerFilteredWallets } = useLedgerFilteredWallets(WALLETS_ALL);
  const { data: cardStatus } = useCardStatus();
  const { cardProvider } = useUserCardProvider();

  const fiatCurrency = useMemo(() => {
    if (cardProvider === cardProviders.MONAVATE) {
      return cardStatus?.displayCurrency || DEFAULT_FIAT_CURRENCY.toUpperCase();
    }
    return userPref?.displayCurrency || DEFAULT_FIAT_CURRENCY.toUpperCase();
  }, [cardProvider, cardStatus, userPref?.displayCurrency]);

  const principalCurrency = useMemo(() => {
    if (fiatCurrency.toLowerCase() === BANK_REQUESTS_COINS.EUR) {
      return BANK_REQUESTS_COINS.EUROC;
    }
    if (
      fiatCurrency.toLowerCase() === BANK_REQUESTS_COINS.USD ||
      (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP &&
        cardProvider === cardProviders.MONAVATE)
    ) {
      return BANK_REQUESTS_COINS.USDT;
    }
    return fiatCurrency;
  }, [fiatCurrency, cardProvider]);

  const [searchValue, setSearchValue] = useState('');

  const handleSearchInput = (event) => {
    setSearchValue(event.target.value);
  };

  const [tobeLinkedWallets, setTobeLinkedWallets] = useState([]);
  const [filteredWallets, setFilteredWallets] = useState([]);

  const { mutate: updateLinkedWallets, isLoading: isLoadingLinkingWallets } = useMutation(
    CARD_UPDATE_LINKED_WALLETS,
    {
      onSettled: (data, error) => {
        sendStats(
          MXP_CARD_MANAGEMENT_CONNECT_ACCOUNT,
          { error, linkedWallets: tobeLinkedWallets },
          FLOWS.CARD_MANAGEMENT
        );
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          getLinkedWallets();
          getLinkableWallets();
        }
        hidePopup();
      },
    }
  );

  useEffect(() => {
    getLinkableWallets();
  }, []);

  const isLoading = isLoadingLinkingWallets || isLoadingRates || isLoadingLinkableWallets;

  useEffect(() => {
    if (linkableWallets && !isLoadingLinkableWallets) {
      const alreadyLinkedWallets = linkableWallets
        .filter((wallet) => wallet.is_linked)
        .sort((a, b) => (a.priority > b.priority ? 1 : -1))
        .map((wallet) => wallet.address_id);

      // Wallets IDs that will be submitted
      setTobeLinkedWallets(alreadyLinkedWallets);

      let _filteredWallets = linkableWallets;
      if (searchValue) {
        _filteredWallets = linkableWallets.filter(
          (wallet) =>
            wallet.coin.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
            currenciesDefault[wallet.coin.toUpperCase()]?.fullName
              ?.toLowerCase()
              .indexOf(searchValue.toLowerCase()) !== -1
        );
      }
      _filteredWallets = _filteredWallets.sort((a, b) => (a.priority > b.priority ? 1 : -1));
      // Display wallets
      setFilteredWallets(_filteredWallets);
    }
  }, [linkableWallets, searchValue]);

  const handleSelect = (event, wallet) => {
    if (event.target.checked) {
      setTobeLinkedWallets([...tobeLinkedWallets, wallet.address_id]);
    } else {
      const selectedWalletIndex = tobeLinkedWallets.indexOf(wallet.address_id);
      const _tobeLinkedWallets = [...tobeLinkedWallets];
      _tobeLinkedWallets.splice(selectedWalletIndex, 1);

      setTobeLinkedWallets(_tobeLinkedWallets);
    }
  };

  const walletsBalances = useMemo(() => {
    if (!isLoadingRates && ledgerFilteredWallets) {
      const result = {};
      ledgerFilteredWallets.forEach((wallet) => {
        let rate = 1;
        if (wallet?.coin.toUpperCase() !== fiatCurrency) {
          rate = BigNumber(rates[`${wallet?.coin?.toUpperCase()}${fiatCurrency}`]?.ask) || 0.0;
        }
        result[wallet.coin] = wallet.available_balance * rate * currencies[wallet.coin].conversion;
      });
      return result;
    }
    return null;
  }, [ledgerFilteredWallets, isLoadingRates]);

  const renderFooter = () => (
    <button
      type="button"
      loading={isLoading ? 'true' : 'false'}
      className="btn btn-primary py-3"
      onClick={() => {
        updateLinkedWallets({ walletsIds: tobeLinkedWallets });
      }}>
      {translate('continue_button_label')}
    </button>
  );

  const renderHeader = () => (
    <div className="form-group rounded bg-light my-4 p-0">
      <input
        type="text"
        value={searchValue}
        onChange={(e) => handleSearchInput(e)}
        className="form-control-search bg-transparent border-0 text-shadow ps-5"
        placeholder={translate('select_wallet_input_placeholder')}
        style={{ height: '2.5rem' }}
      />
      <span className="icon-search icon-search-other text-shadow" />
    </div>
  );

  const cryptodraftWallet = filteredWallets.find((w) => w?.is_cryptodraft);
  const cryptodraftDisplayRate = useRate(cryptodraftWallet?.coin, principalCurrency);

  const renderWalletItem = (wallet) => {
    const isCryptodraft = !!cryptodraftDetails && wallet?.is_cryptodraft;
    let balance;
    let currency;

    if (isCryptodraft) {
      const cryptodraftAvailableBalance =
        wallet?.available_balance * currencies[wallet?.coin].conversion * cryptodraftDisplayRate;

      balance = formatNumber(
        cryptodraftAvailableBalance > 0 ? cryptodraftAvailableBalance : 0,
        exchange.FIAT_VALUE
      );
      currency = principalCurrency?.toUpperCase();
    } else if (walletsBalances) {
      balance = formatNumber(walletsBalances[wallet?.coin], exchange.FIAT_VALUE);
      currency = fiatCurrency?.toUpperCase();
    }

    const walletBalanceLabel = `${balance} ${currency}`;

    return (
      <WalletWithCheckbox
        key={wallet.address_id}
        isChecked={tobeLinkedWallets.indexOf(wallet.address_id) >= 0}
        walletIcon={wallet?.coin || 'BTC'}
        isCryptodraft={isCryptodraft}
        handleSelect={(e) => handleSelect(e, wallet)}>
        <b className={isCryptodraft ? 'capitalize' : 'upper-case'}>
          {isCryptodraft ? translate('cryptodraft_spending_wallet_name') : wallet.coin}
        </b>
        {walletsBalances && (
          <p className="text-shadow text-f6 custom-subtext">{walletBalanceLabel}</p>
        )}
      </WalletWithCheckbox>
    );
  };

  return (
    <ModalBodyLayout
      title={translate('add_account_label')}
      renderFooter={isBrowser && renderFooter}
      renderHeader={renderHeader}
      withBack={false}>
      {isLoading ? (
        <div className="wallet-list">
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
          <WalletPopupItemPlaceholder />
        </div>
      ) : (
        <div className="wallet-list">
          {filteredWallets.map((wallet) => renderWalletItem(wallet))}
          {isMobile && (
            <button
              type="button"
              loading={isLoading ? 'true' : 'false'}
              className="btn btn-primary py-3 w-100 mt-3"
              onClick={() => {
                updateLinkedWallets({ walletsIds: tobeLinkedWallets });
              }}>
              {translate('continue_button_label')}
            </button>
          )}
        </div>
      )}
    </ModalBodyLayout>
  );
};

export default LinkWallets;

LinkWallets.propTypes = {
  hidePopup: PropTypes.func.isRequired,
  cryptodraftDetails: PropTypes.oneOfType([PropTypes.object]),
};

LinkWallets.defaultProps = {
  cryptodraftDetails: null,
};
